/**
 * Drop-in voice samples.
 *
 * Mirrors the fail-soft convention `loadParts`/`loadRig` already use
 * (src/three/parts.js): probe for something optional, and if it isn't there,
 * quietly carry on rather than throwing. There is no manifest to keep in
 * sync — a pool is whatever numbered files a HEAD request actually finds,
 * which is what lets someone drop a file into public/audio/sfx/ and have it
 * picked up with nothing else to regenerate.
 *
 * Numbers must be contiguous starting at 1: the probe stops at the first
 * gap, so 1.mp3 + 3.mp3 with no 2.* only ever resolves to 1.mp3. That's a
 * deliberate simplicity trade — good enough for a handful of variants per
 * pool, and it keeps the probe itself trivial.
 */

const EXTENSIONS = ['mp3', 'wav', 'ogg'];
const MAX_VARIANTS = 8;

const base = () => `${import.meta.env.BASE_URL}audio/sfx`;

/** event/pool -> Promise<string[]> of resolved URLs, including empty lists. */
const cache = new Map();

async function exists(url) {
  try {
    const res = await fetch(url, { method: 'HEAD' });
    // `res.ok` alone isn't enough: both the dev server and (absent the nginx
    // rule this ships with, docker/nginx.conf.template's `location /audio/`)
    // a static host's SPA fallback answer a missing path with a 200 serving
    // index.html rather than a 404. That response is always text/html; no
    // real audio file ever is, so it's what actually tells "missing" apart
    // from "found" here.
    const type = res.headers.get('content-type') ?? '';
    return res.ok && !type.includes('text/html');
  } catch {
    return false;
  }
}

async function probe(event, pool) {
  const urls = [];
  for (let n = 1; n <= MAX_VARIANTS; n++) {
    let found = null;
    for (const ext of EXTENSIONS) {
      const url = `${base()}/${event}/${pool}/${n}.${ext}`;
      if (await exists(url)) { found = url; break; }
    }
    if (!found) break;
    urls.push(found);
  }
  return urls;
}

/** Resolved URLs for one event/pool, probed once and cached thereafter. */
function poolFor(event, pool) {
  const key = `${event}/${pool}`;
  let entry = cache.get(key);
  if (!entry) {
    entry = probe(event, pool);
    cache.set(key, entry);
  }
  return entry;
}

/**
 * A random sample URL for this event/pool, or null if that pool is empty.
 * Always awaits the pool's probe (started here or already in flight from
 * `preloadSamplePools`), so there is nothing to race.
 */
export async function pickSample(event, pool) {
  const urls = await poolFor(event, pool);
  if (!urls.length) return null;
  return urls[Math.floor(Math.random() * urls.length)];
}

/** Kicks off every pool's probe at boot, so the first real sound of each
 * kind doesn't pay for it — call once, result intentionally discarded. */
export function preloadSamplePools(events, pools) {
  for (const event of events) for (const pool of pools) poolFor(event, pool);
}
